import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Spinner, CheckCircle, Warning, XCircle } from "@phosphor-icons/react";

/**
 * Dialog progres + hasil untuk semua impor Excel/CSV.
 * props:
 *  - open: bool
 *  - loading: bool — true saat proses berjalan (spinner + timer, dialog tidak bisa ditutup)
 *  - fileName: string
 *  - result: { created, updated, skipped, imported, total, errors, skipped_details } | null
 *  - onClose: () => void
 */
export default function ImportProgressDialog({ open, loading, fileName, result, onClose }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!loading) { setElapsed(0); return; }
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [loading]);

  const r = result;
  const errs = (r?.errors || []).length;
  const cards = r ? [
    r.created !== undefined && { label: "Baru", value: r.created, cls: "border-emerald-200 bg-emerald-50", txt: "text-emerald-800", sub: "text-emerald-700" },
    r.updated !== undefined && { label: "Diperbarui", value: r.updated, cls: "border-sky-200 bg-sky-50", txt: "text-sky-800", sub: "text-sky-700" },
    r.imported !== undefined && { label: "Berhasil", value: r.imported, cls: "border-emerald-200 bg-emerald-50", txt: "text-emerald-800", sub: "text-emerald-700" },
    (r.skipped !== undefined) && { label: "Dilewati", value: r.skipped, cls: "border-stone-200 bg-stone-50", txt: "text-stone-700", sub: "text-stone-600" },
    { label: "Gagal", value: errs, cls: errs ? "border-rose-200 bg-rose-50" : "border-stone-200 bg-stone-50", txt: errs ? "text-rose-800" : "text-stone-700", sub: errs ? "text-rose-700" : "text-stone-600" },
  ].filter(Boolean) : [];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !loading) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0" data-testid="import-progress-dialog">
        <DialogHeader className="shrink-0 border-b border-slate-200 px-6 pt-6 pb-3">
          <DialogTitle>{loading ? "Mengimpor Data…" : "Hasil Impor"}</DialogTitle>
        </DialogHeader>
        <div className="min-h-0 overflow-y-auto px-6 pt-4 pb-6">
          {loading ? (
            <div className="py-8 flex flex-col items-center gap-3 text-center" data-testid="import-loading">
              <Spinner size={36} className="animate-spin text-amber-600" />
              <div className="text-sm font-medium text-slate-700">Proses impor sedang berlangsung</div>
              {fileName && <div className="text-xs text-slate-500 font-mono-plex">{fileName}</div>}
              <div className="text-xs text-slate-400 tabular-nums" data-testid="import-elapsed">{elapsed} detik</div>
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-1.5">
                Jangan tutup atau refresh halaman ini sampai proses selesai.
              </div>
            </div>
          ) : r ? (
            <div className="space-y-3" data-testid="import-result">
              <div className="grid grid-cols-3 gap-2">
                {cards.map((c) => (
                  <div key={c.label} className={`rounded-md border p-2.5 ${c.cls}`}>
                    <div className={`text-[10px] uppercase tracking-wider ${c.sub}`}>{c.label}</div>
                    <div className={`text-xl font-medium tabular-nums ${c.txt}`} data-testid={`import-result-${c.label.toLowerCase()}`}>{c.value}</div>
                  </div>
                ))}
              </div>
              {r.total !== undefined && <div className="text-xs text-slate-500">Total baris diproses: <b>{r.total}</b></div>}
              {(r.skipped_details || []).length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 space-y-0.5 max-h-40 overflow-y-auto" data-testid="import-skipped-list">
                  <div className="font-medium flex items-center gap-1 mb-1"><Warning size={13} weight="fill" />Baris yang dilewati:</div>
                  {r.skipped_details.map((d, i) => <div key={i}>• {d}</div>)}
                </div>
              )}
              {errs > 0 && (
                <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700 space-y-0.5 max-h-40 overflow-y-auto" data-testid="import-error-list">
                  <div className="font-medium flex items-center gap-1 mb-1"><XCircle size={13} weight="fill" />Kesalahan:</div>
                  {r.errors.map((e, i) => <div key={i}>• {e}</div>)}
                </div>
              )}
              {errs === 0 && (r.created || r.imported || r.updated) > 0 && (
                <div className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle size={13} weight="fill" />Impor selesai tanpa kesalahan.</div>
              )}
            </div>
          ) : null}
        </div>
        {!loading && r && (
          <div className="sticky bottom-0 z-10 -mx-6 -mb-6 mt-2 border-t border-slate-200 bg-background px-6 py-3">
            <Button onClick={onClose} className="w-full bg-slate-900" data-testid="import-result-close">Tutup</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
